/* eslint-disable max-lines-per-function */
import { useCallback, useEffect, useRef, useState } from "react";
import { COUNTRY_OPTIONS } from "../../../utils/countryMapping";
import { LANGUAGE_OPTIONS } from "../../../utils/languageMapping";

/**
 * Search result from API
 */
export interface SearchResult {
  id: string;
  slug: string;
  name: string;
  synopsis: string | null;
  year: number | null;
  posterUrlHd: string | null;
  backdropUrlHd: string | null;
  horizontalTileUrl: string | null;
  verticalTileUrl: string | null;
  genres: string[];
  directorName: string | null;
  voteAverage: number | null;
  runtimeMinutes: number | null;
  country: string | null;
  languages: string[];
  relevance: number;
  matchType: "title" | "director" | "cast" | "synopsis" | "country" | "language" | "fuzzy";
  matchContext: string | null;
}

/**
 * Search suggestion from API
 */
export interface SearchSuggestion {
  type: "title" | "director" | "cast" | "country";
  value: string;
  metadata?: {
    titleId?: string;
    slug?: string;
    year?: number;
  };
}

/**
 * Pagination metadata
 */
export interface SearchPagination {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

/**
 * Search API response
 */
interface SearchResponse {
  results: SearchResult[];
  pagination: SearchPagination;
}

/**
 * Suggestions API response
 */
interface SuggestionsResponse {
  suggestions: SearchSuggestion[];
}

/**
 * useSearch hook options
 */
interface UseSearchOptions {
  /** Debounce delay in milliseconds (default: 300ms) */
  debounceMs?: number;
  /** Suggestion debounce delay in milliseconds (default: 150ms) */
  suggestionsDebounceMs?: number;
  /** Results per page (default: 20) */
  limit?: number;
  /** Auto-execute search on mount if query provided (default: false) */
  immediate?: boolean;
}

/**
 * Preprocess search query to handle country and language name mappings
 * If user types "France" or "French", also search for "FR" and "fr"
 */
function preprocessSearchQuery(query: string): string[] {
  const trimmedQuery = query.trim();
  const queries: string[] = [trimmedQuery];

  // Check exact country name match
  const countryMatch = COUNTRY_OPTIONS.find((opt) => opt.label.toLowerCase() === trimmedQuery.toLowerCase());
  if (countryMatch) {
    queries.push(countryMatch.value, countryMatch.value.toLowerCase());
  }

  // Check exact language name match
  const languageMatch = LANGUAGE_OPTIONS.find((opt) => opt.label.toLowerCase() === trimmedQuery.toLowerCase());
  if (languageMatch) {
    queries.push(languageMatch.value, languageMatch.value.toUpperCase());
  }

  // Check partial country name matches (for queries like "Fr")
  if (trimmedQuery.length >= 2) {
    const partialCountryMatches = COUNTRY_OPTIONS.filter((opt) =>
      opt.label.toLowerCase().startsWith(trimmedQuery.toLowerCase()),
    ).slice(0, 3);

    for (const match of partialCountryMatches) {
      if (!queries.includes(match.value)) {
        queries.push(match.value, match.value.toLowerCase());
      }
    }
  }

  return [...new Set(queries)];
}

/**
 * Fetch search results from API
 * Tries preprocessing for country/language names, falls back to original query
 */
async function fetchSearchResults(
  searchQuery: string,
  limit: number,
  offset: number,
  signal: AbortSignal,
): Promise<SearchResponse> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  // Preprocess query to handle country/language names
  const queries = preprocessSearchQuery(searchQuery);

  // If preprocessing didn't add any variants, just do a single request
  if (queries.length === 1) {
    const url = new URL(`${baseUrl}/v1/search`);
    url.searchParams.set("q", searchQuery);
    url.searchParams.set("limit", String(limit));
    url.searchParams.set("offset", String(offset));

    const response = await fetch(url.toString(), { signal });
    if (!response.ok) {
      throw new Error(`Search failed: ${response.statusText}`);
    }
    return response.json() as Promise<SearchResponse>;
  }

  // Try each preprocessed query variant
  for (const query of queries) {
    try {
      const url = new URL(`${baseUrl}/v1/search`);
      url.searchParams.set("q", query);
      url.searchParams.set("limit", String(limit));
      url.searchParams.set("offset", String(offset));

      const response = await fetch(url.toString(), { signal });
      if (!response.ok) continue;

      const data = (await response.json()) as SearchResponse;

      // Return first successful result
      if (data.results.length > 0) {
        return data;
      }
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        throw err;
      }
      // Try next query variant
      continue;
    }
  }

  // If all queries failed, return empty results instead of error
  // This provides better UX than showing an error message
  return {
    results: [],
    pagination: {
      total: 0,
      limit,
      offset,
      hasMore: false,
    },
  };
}

/**
 * Fetch search suggestions from API
 */
async function fetchSuggestions(searchQuery: string, limit: number, signal: AbortSignal): Promise<SuggestionsResponse> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";
  const url = new URL(`${baseUrl}/v1/search/suggestions`);
  url.searchParams.set("q", searchQuery);
  url.searchParams.set("limit", String(limit));

  const response = await fetch(url.toString(), { signal });

  if (!response.ok) {
    throw new Error(`Suggestions failed: ${response.statusText}`);
  }

  return response.json() as Promise<SuggestionsResponse>;
}

/**
 * Custom hook for search functionality with debouncing and pagination
 */
export function useSearch(options: UseSearchOptions = {}) {
  const { debounceMs = 300, suggestionsDebounceMs = 150, limit = 20, immediate = false } = options;

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [pagination, setPagination] = useState<SearchPagination | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const suggestionsTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const suggestionsAbortControllerRef = useRef<AbortController | null>(null);
  const lastQueryRef = useRef<string>("");

  /**
   * Execute search API call
   */
  const executeSearch = useCallback(
    async (searchQuery: string, offset = 0) => {
      if (!searchQuery.trim()) {
        setResults([]);
        setPagination(null);
        setLoading(false);
        return;
      }

      // Skip if this is the same query at offset 0 (avoid redundant searches)
      if (offset === 0 && lastQueryRef.current === searchQuery.trim()) {
        return;
      }
      lastQueryRef.current = searchQuery.trim();

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      setLoading(true);
      setError(null);

      try {
        const data = await fetchSearchResults(searchQuery, limit, offset, abortControllerRef.current.signal);
        setResults(data.results);
        setPagination(data.pagination);
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          return;
        }
        const message = err instanceof Error ? err.message : "Search failed";
        setError(message);
        setResults([]);
        setPagination(null);
      } finally {
        setLoading(false);
      }
    },
    [limit],
  );

  /**
   * Debounced search function
   */
  const search = useCallback(
    (searchQuery: string) => {
      setQuery(searchQuery);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        void executeSearch(searchQuery, 0);
      }, debounceMs);
    },
    [executeSearch, debounceMs],
  );

  /**
   * Load more results (pagination)
   */
  const loadMore = useCallback(() => {
    if (!pagination?.hasMore || loading) return;
    void executeSearch(query, pagination.offset + pagination.limit);
  }, [pagination, loading, query, executeSearch]);

  /**
   * Clear search results
   */
  const clear = useCallback(() => {
    setQuery("");
    setResults([]);
    setPagination(null);
    setError(null);
    setSuggestions([]);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (suggestionsTimerRef.current) {
      clearTimeout(suggestionsTimerRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (suggestionsAbortControllerRef.current) {
      suggestionsAbortControllerRef.current.abort();
    }
  }, []);

  /**
   * Fetch suggestions (faster debounce for autocomplete)
   */
  const getSuggestions = useCallback(
    (searchQuery: string) => {
      if (suggestionsTimerRef.current) {
        clearTimeout(suggestionsTimerRef.current);
      }
      if (searchQuery.trim().length < 2) {
        setSuggestions([]);
        return;
      }
      suggestionsTimerRef.current = setTimeout(() => {
        void (async () => {
          if (suggestionsAbortControllerRef.current) {
            suggestionsAbortControllerRef.current.abort();
          }
          suggestionsAbortControllerRef.current = new AbortController();
          setSuggestionsLoading(true);
          try {
            const data = await fetchSuggestions(searchQuery, 10, suggestionsAbortControllerRef.current.signal);
            setSuggestions(data.suggestions);
          } catch (err) {
            if (err instanceof Error && err.name === "AbortError") {
              return;
            }
            setSuggestions([]);
          } finally {
            setSuggestionsLoading(false);
          }
        })();
      }, suggestionsDebounceMs);
    },
    [suggestionsDebounceMs],
  );

  /**
   * Execute immediate search on mount if query provided
   */
  useEffect(() => {
    if (immediate && query) {
      void executeSearch(query, 0);
    }
  }, [immediate]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (suggestionsTimerRef.current) clearTimeout(suggestionsTimerRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (suggestionsAbortControllerRef.current) suggestionsAbortControllerRef.current.abort();
    };
  }, []);

  return {
    query,
    results,
    pagination,
    loading,
    error,
    search,
    loadMore,
    clear,
    suggestions,
    suggestionsLoading,
    getSuggestions,
  };
}
